import React from "react";
import Hints from "../Hints";
import FeedbackTab from "./FeedbackTab";
import type { Category, FormStructure } from "../../../types/form";
import type {
  FeedbackBatchDetail,
  FeedbackItemDetail,
} from "../../../API/feedbackAPI";

export interface ActiveHint {
  title?: string;
  content: string;
}

export type DrawerTab = "hints" | "feedback";

interface FormDrawerProps {
  drawerOpen: boolean;
  activeCategory: Category | null;
  formStructure: FormStructure | null;
  /** Current form field values (for hints assistant read-back). */
  formData: Record<string, unknown>;
  activeHint: ActiveHint | null;
  onClose: () => void;
  drawerTab: DrawerTab;
  onTabChange: (tab: DrawerTab) => void;
  applicationId: number | undefined;
  formId: number | undefined;
  feedbackBatch: FeedbackBatchDetail | null;
  feedbackLoading?: boolean;
  onFeedbackBatchUpdated: (batch: FeedbackBatchDetail | null) => void;
  /** Jump main form to the question and highlight it (feedback card click). */
  onFeedbackNavigateToSource?: (item: FeedbackItemDetail) => void;
  highlightedFeedbackItemId?: number | null;
  hasActionRequiredFeedback?: boolean;
  selectCategoryById: (categoryId: number) => boolean;
  navigateToCategoryContainingQuestion: (questionId: number) => boolean;
  onEnsureHintsOpen?: () => void;
  /** When false, Feedback tab and panel are hidden (no reviewer notes for this form). */
  showFeedbackTab?: boolean;
  /** Auto-save current section before resolve / resubmit. */
  onSaveBeforeFeedbackAction?: () => Promise<void>;
  /** Notifies parent which feedback item is active (for inline card). */
  onActiveFeedbackItemChange?: (item: FeedbackItemDetail | null) => void;
}

const FormDrawer: React.FC<FormDrawerProps> = ({
  drawerOpen,
  activeCategory,
  formStructure,
  formData,
  activeHint,
  onClose,
  drawerTab,
  onTabChange,
  applicationId,
  formId,
  feedbackBatch,
  feedbackLoading = false,
  onFeedbackBatchUpdated,
  onFeedbackNavigateToSource,
  highlightedFeedbackItemId = null,
  hasActionRequiredFeedback = false,
  selectCategoryById,
  navigateToCategoryContainingQuestion,
  onEnsureHintsOpen,
  showFeedbackTab = false,
  onSaveBeforeFeedbackAction,
  onActiveFeedbackItemChange,
}) => {
  const showFeedbackPanel = showFeedbackTab && drawerTab === "feedback";
  if (!drawerOpen) {
    return (
      <div className="w-0 shrink-0 overflow-hidden transition-all duration-300" />
    );
  }

  return (
    <div
      className="w-full md:w-[30%] max-w-none md:max-w-[440px] shrink-0 self-start md:sticky md:top-[40px] transition-all duration-300 bg-white shadow-[0px_1px_2px_0px_#0000000D] rounded-lg flex flex-col max-h-[min(70vh,36rem)] md:max-h-[80vh] overflow-hidden p-6 pb-4"
      style={{ minWidth: 0 }}
    >
      <div className="flex justify-between items-center mb-3 shrink-0 gap-2">
        {showFeedbackTab ? (
          <div className="flex gap-1 p-1 bg-[#F4F5F7] rounded-lg">
            <button
              type="button"
              onClick={() => onTabChange("hints")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                drawerTab === "hints"
                  ? "bg-white text-[#1B365D] shadow-sm"
                  : "text-[#4B5563] hover:text-[#1B365D]"
              }`}
            >
              Hints
            </button>
            <button
              type="button"
              onClick={() => onTabChange("feedback")}
              className={`relative px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                drawerTab === "feedback"
                  ? "bg-white text-[#1B365D] shadow-sm"
                  : "text-[#4B5563] hover:text-[#1B365D]"
              }`}
            >
              Feedback
              {hasActionRequiredFeedback && (
                <span
                  className="absolute -top-1 right-3 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
                  title="Action required"
                  aria-label="Action required"
                />
              )}
            </button>
          </div>
        ) : (
          <span className="text-sm font-semibold text-[#1B365D] px-1">Hints</span>
        )}
        <button
          onClick={onClose}
          className="text-gray-600 hover:text-red-500 text-base bg-[#F4F5F7] h-[30px] w-[30px] rounded-full shrink-0"
        >
          ✕
        </button>
      </div>
      {showFeedbackPanel ? (
        // Scroll lives inside FeedbackTab (max-h + overflow-y); avoid double scrollbars on this wrapper.
        <div className="flex flex-1 min-h-0 flex-col">
          <FeedbackTab
            applicationId={applicationId}
            formId={formId}
            activeCategoryTitle={activeCategory?.name ?? null}
            batch={feedbackBatch}
            loading={feedbackLoading}
            onBatchUpdated={onFeedbackBatchUpdated}
            onNavigateToSource={onFeedbackNavigateToSource}
            highlightedItemId={highlightedFeedbackItemId}
            onSaveBeforeFeedbackAction={onSaveBeforeFeedbackAction}
            onActiveFeedbackItemChange={onActiveFeedbackItemChange}
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Hints
            activeSection={activeCategory as any}
            activeHint={activeHint}
            formData={formData}
            formStructure={formStructure}
            formId={formId}
            selectCategoryById={selectCategoryById}
            navigateToCategoryContainingQuestion={
              navigateToCategoryContainingQuestion
            }
            onEnsureHintsOpen={onEnsureHintsOpen}
          />
        </div>
      )}
    </div>
  );
};

export default FormDrawer;
